import { CalendarNav, CalendarNext, CalendarPrev, CalendarToday } from '@mobiscroll/react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { z } from 'zod';

import { calendarColors } from '../../../theme/colorPalette';
import { localStorageApi, localStorageKeys } from '../../../utils';
import { Legend } from '../Legend';

const legendElements = [
  { label: 'Blockung', depiction: calendarColors.internalBlockung },
  { label: 'Aktuelle Auswahl', depiction: calendarColors.selectedBlockung },
  { label: 'Nicht verfügbar', depiction: calendarColors.notBookable },
];

type ResourceCalendarHeaderProps = {
  readonly weekCount: number | undefined;
  readonly onWeekCountChange: (viewSize: number) => void;
  readonly view: 'week' | 'day';
  readonly setView: (view: 'week' | 'day') => void;
};

export const ResourceCalendarHeader = ({
  weekCount,
  onWeekCountChange,
  view,
  setView,
}: ResourceCalendarHeaderProps) => {
  const maxWeekCount = 12;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <CalendarToday />

        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: '.9em' }}>
          <CalendarPrev />
          <CalendarNav />
          <CalendarNext />
        </Box>
      </Box>

      <Stack direction="row" gap={2}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView: unknown) => {
            const parsedView = z.enum(['day', 'week']).parse(newView);
            setView(parsedView);
            if (parsedView === 'week') {
              onWeekCountChange(localStorageApi.get(localStorageKeys.RESOURCE_CALENDAR_WEEK_COUNT) ?? 2);
            } else {
              onWeekCountChange(7);
            }
          }}
          aria-label="Kalendar Ansicht"
          size="small"
        >
          <ToggleButton value="week">Wochenansicht</ToggleButton>
          <ToggleButton value="day">Tagesansicht</ToggleButton>
        </ToggleButtonGroup>

        <FormControl sx={{ width: '110px' }}>
          <InputLabel id="weekdays-select-label">{view === 'week' ? 'Wochenanzahl' : 'Tagesanzahl'}</InputLabel>
          <Select
            labelId="weekdays-select-label"
            value={weekCount}
            label={view === 'week' ? 'Wochenanzahl' : 'Tagesanzahl'}
            onChange={(event) => {
              if (view === 'week') {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
                localStorageApi.set(localStorageKeys.RESOURCE_CALENDAR_WEEK_COUNT, Number(event.target.value));
              }

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
              onWeekCountChange(Number(event.target.value));
            }}
            size="small"
            variant="outlined"
          >
            {Array.from({ length: maxWeekCount }).map((_, i) => (
              <MenuItem key={`weekdays-select-item-${i + 1}`} value={i + 1}>
                {i + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Legend elements={legendElements} />
      </Stack>
    </Box>
  );
};
